/**
 * Loading, failure and empty are different facts and look different.
 *
 *   loading  a static skeleton in the shape of what is coming (role=status),
 *            so the layout does not jump when it arrives. No shimmer loop.
 *   error    an alert with Try again. `status` undefined means the request
 *            never reached the server; it is never shown as empty or zero.
 *   empty    three kinds: `none` (the source has nothing in this scope),
 *            `filtered` (filters exclude everything; offer to clear them),
 *            `not-recorded` (the source does not record this at all).
 *
 * Optional props (Tide phase 2, for the work queues; every one is additive and
 * its absence is the phase 1 behaviour):
 *   loading  `busy` false drops aria-busy (a busy live region may withhold its
 *            announcement); `srOnlyLabel` keeps the label for assistive
 *            technology only; `announce` false drops role=status, for a second
 *            skeleton beside one that already speaks.
 *   error    `heading` replaces the status-derived heading (a refusal names its
 *            gate); `headingLevel` renders it as a real heading; `detail` sits
 *            under the message; `tone` 'withheld' draws a refusal rather than a
 *            failure (the withheld status colour and a square marker).
 *   empty    `kindLabel` replaces the caption ("Nothing waiting" for a queue).
 */
import React from 'react';
import { Marker } from './Marker';
import { STATUS } from './tokens';
import { Button } from '../shadcn/ui/button';

export type SkeletonShape = 'overview' | 'chart' | 'table' | 'tiles' | 'lines';
export type EmptyReason = 'none' | 'filtered' | 'not-recorded';

export type StateBlockProps =
  | { kind: 'loading'; shape?: SkeletonShape; label?: string; busy?: boolean; srOnlyLabel?: boolean; announce?: boolean }
  | {
    kind: 'error'; message?: string; status?: number; onRetry?: () => void;
    heading?: string; headingLevel?: 2 | 3 | 4; detail?: React.ReactNode; tone?: 'danger' | 'withheld';
  }
  | { kind: 'empty'; reason?: EmptyReason; message: React.ReactNode; onClear?: () => void; clearLabel?: string; kindLabel?: string };

/** A short name for the failure class; the message carries the detail. */
export function failureHeading(status: number | undefined): string {
  if (status === undefined) return 'Server not reached';
  if (status === 401 || status === 403) return 'No access';
  if (status === 404) return 'Not found';
  if (status >= 500) return 'Server error';
  return 'Not loaded';
}

const DEFAULT_FAILURE: Record<string, string> = {
  'Server not reached': 'Could not reach the server, so nothing here is current.',
  'No access': 'You do not have access to this.',
  'Not found': 'That was not found.',
  'Server error': 'The server failed while assembling this. It is not an empty result.',
  'Not loaded': 'This could not be loaded.',
};

const EMPTY_KIND: Record<EmptyReason, string> = {
  none: 'Nothing recorded',
  filtered: 'No matches',
  'not-recorded': 'Not recorded by the source',
};

function Skeleton({ shape }: { shape: SkeletonShape }) {
  const tiles = (n: number) => (
    <div className="tide-skel-grid">{Array.from({ length: n }, (_, i) => <div key={i} className="tide-skel tide-skel--tile" />)}</div>
  );
  const rows = (n: number) => (
    <div style={{ display: 'grid', gap: 6 }}>{Array.from({ length: n }, (_, i) => <div key={i} className="tide-skel tide-skel--row" />)}</div>
  );
  switch (shape) {
    case 'overview':
      return <>{tiles(8)}<div className="tide-skel tide-skel--chart" /><div className="tide-skel-grid"><div className="tide-skel tide-skel--chart" /><div className="tide-skel tide-skel--chart" /></div></>;
    case 'tiles':
      return tiles(4);
    case 'table':
      return rows(8);
    case 'lines':
      return <div style={{ display: 'grid', gap: 10 }}>{[90, 70, 80, 55, 75].map((w) => <div key={w} className="tide-skel tide-skel--line" style={{ width: `${w}%` }} />)}</div>;
    case 'chart':
    default:
      return <><div className="tide-skel tide-skel--chart" />{rows(4)}</>;
  }
}

export default function StateBlock(props: StateBlockProps) {
  if (props.kind === 'loading') {
    const announce = props.announce !== false;
    return (
      <div role={announce ? 'status' : undefined} className="tide-state tide-state--loading" aria-busy={props.busy === false ? undefined : 'true'} data-shape={props.shape || 'chart'}>
        {(announce || !props.srOnlyLabel) && (
          <p className={props.srOnlyLabel ? 'tide-state__loading-label tide-sr' : 'tide-state__loading-label'}>{props.label || 'Loading…'}</p>
        )}
        <div aria-hidden="true" style={{ display: 'grid', gap: 16 }}><Skeleton shape={props.shape || 'chart'} /></div>
      </div>
    );
  }

  if (props.kind === 'error') {
    const derived = failureHeading(props.status);
    const heading = props.heading || derived;
    const withheld = props.tone === 'withheld';
    const head = (
      <>
        <Marker shape={withheld ? 'square' : 'triangle'} color={withheld ? STATUS.withheld.fg : STATUS.danger.fg} size={12} />
        {heading}
      </>
    );
    const H = props.headingLevel ? (`h${props.headingLevel}` as 'h2' | 'h3' | 'h4') : null;
    return (
      <div role="alert" className="tide-state tide-state--error" data-status={props.status ?? 'unreached'} data-tone={withheld ? 'withheld' : 'danger'}>
        {H ? <H className="tide-state__head">{head}</H> : <span className="tide-state__head">{head}</span>}
        <p className="tide-state__msg">{props.message || DEFAULT_FAILURE[derived]}</p>
        {props.detail !== undefined && props.detail !== null && <div className="tide-state__detail">{props.detail}</div>}
        {props.onRetry && <Button type="button" variant="outline" size="sm" className="tide-state__btn" onClick={props.onRetry}>Try again</Button>}
      </div>
    );
  }

  const reason = props.reason || 'none';
  return (
    <div className="tide-state tide-state--empty" data-reason={reason}>
      <span className="tide-state__kind">
        <Marker shape={reason === 'not-recorded' ? 'dash' : 'circle'} color={reason === 'not-recorded' ? STATUS.missing.fg : 'var(--tide-boundary)'} size={10} />
        {props.kindLabel || EMPTY_KIND[reason]}
      </span>
      <p className="tide-state__msg">{props.message}</p>
      {reason === 'filtered' && props.onClear && (
        <Button type="button" variant="outline" size="sm" className="tide-state__btn" onClick={props.onClear}>{props.clearLabel || 'Clear filters'}</Button>
      )}
    </div>
  );
}
